<script setup lang="ts">
import type { Property } from "~/classes/entity"

import { propertyIcon, propertyName, PropertySchema } from "~/classes/entity"

const emit = defineEmits(["remove"])
const facet = defineModel<SearchFacet>("facet")
const { searchFacetType, searchFacetTypeName } = useInstantMenu()
const { showableProperties } = useViewMode()
const name = computed(() => propertyName(facet.value?.propertyId as Property))
const icon = computed(() => propertyIcon(facet.value?.propertyId as Property))

const onRemoveFilter = () => {
  emit("remove")
}

const facetSchema = computed(() => {
  if (!isDefined(facet.value))
    return
  return showableProperties[facet.value.propertyId as Property]?.schema
})

function updateFacetType(key: SearchFacetTypeKey): SearchFacetType | undefined {
  if (!isDefined(facet.value)) {
    console.error("Facet is not defined")
    return
  }
  if (key === "exists")
    facet.value.facetType = { exists: true }
  else if (key === "equals")
    facet.value.facetType = { equals: "" }
  else if (key === "notEquals")
    facet.value.facetType = { notEquals: "" }
  else if (key === "startsWith")
    facet.value.facetType = { startsWith: "" }
  else if (key === "contains")
    facet.value.facetType = { contains: "" }
  else if (key === "greaterThan")
    facet.value.facetType = { greaterThan: 0 }
  else if (key === "greaterThanEqual")
    facet.value.facetType = { greaterThanEqual: 0 }
  else if (key === "lessThan")
    facet.value.facetType = { lessThan: 0 }
  else if (key === "lessThanEqual")
    facet.value.facetType = { lessThanEqual: 0 }
  else if (key === "afterDate")
    facet.value.facetType = { afterDate: Date.now() }
  else if (key === "onOrAfterDate")
    facet.value.facetType = { onOrAfterDate: Date.now() }
  else if (key === "beforeDate")
    facet.value.facetType = { beforeDate: Date.now() }
  else if (key === "onOrBeforeDate")
    facet.value.facetType = { onOrBeforeDate: Date.now() }
  else
    console.error("Facet type is not allowed here", key)
}
</script>

<template>
  <div class="filter-picker-container">
    <div class="filter-picker-description">
      <icon-property :type="icon" size="2xs" />
      <div class="name">
        {{ name }}
      </div>
    </div>
    <dropdown
      v-if="facet"
      anchor-x="left"
      anchor-y="bottom"
      flow-x="right"
      flow-y="down"
    >
      <template #default="{ toggle }">
        <div
          class="filter-picker-button"
          @click="toggle()"
        >
          <template v-if="'exists' in facet.facetType">
            <div class="filter-type-glyph">
              exists
            </div>
          </template>
          <template v-if="'equals' in facet.facetType">
            <div class="filter-type-glyph">
              is equal to
            </div>
            <div class="filter-type-content">
              &OpenCurlyDoubleQuote;{{ facet.facetType.equals }}&CloseCurlyDoubleQuote;
            </div>
          </template>
          <template v-else-if="'notEquals' in facet.facetType">
            <div class="filter-type-glyph">
              is not equal to
            </div>
            <div class="filter-type-content">
              {{ facet.facetType.notEquals }}
            </div>
          </template>
          <template v-else-if="'greaterThan' in facet.facetType">
            <div class="filter-type-glyph">
              is greater than
            </div>
            <div class="filter-type-content">
              {{ facet.facetType.greaterThan }}
            </div>
          </template>
          <template v-else-if="'afterDate' in facet.facetType">
            <div class="filter-type-glyph">
              is after
            </div>
            <div class="filter-type-content">
              {{ new Date(facet.facetType.afterDate).toUTCString() }}
            </div>
          </template>
          <template v-else-if="'greaterThanEqual' in facet.facetType">
            <div class="filter-type-glyph">
              is greater than or equal to
            </div>
            <div class="filter-type-content">
              {{ facet.facetType.greaterThanEqual }}
            </div>
          </template>
          <template v-else-if="'onOrAfterDate' in facet.facetType">
            <div class="filter-type-glyph">
              is on or after
            </div>
            <div class="filter-type-content">
              {{ new Date(facet.facetType.onOrAfterDate).toUTCString() }}
            </div>
          </template>
          <template v-else-if="'lessThan' in facet.facetType">
            <div class="filter-type-glyph">
              is less than
            </div>
            <div class="filter-type-content">
              {{ facet.facetType.lessThan }}
            </div>
          </template>
          <template v-else-if="'beforeDate' in facet.facetType">
            <div class="filter-type-glyph">
              is before
            </div>
            <div class="filter-type-content">
              {{ new Date(facet.facetType.beforeDate).toUTCString() }}
            </div>
          </template>
          <template v-else-if="'lessThanEqual' in facet.facetType">
            <div class="filter-type-glyph">
              is less than or equal to
            </div>
            <div class="filter-type-content">
              {{ facet.facetType.lessThanEqual }}
            </div>
          </template>
          <template v-else-if="'onOrBeforeDate' in facet.facetType">
            <div class="filter-type-glyph">
              is on or before
            </div>
            <div class="filter-type-content">
              {{ new Date(facet.facetType.onOrBeforeDate).toUTCString() }}
            </div>
          </template>
          <template v-else-if="'startsWith' in facet.facetType">
            <div class="filter-type-glyph">
              starts with
            </div>
            <div class="filter-type-content">
              &OpenCurlyDoubleQuote;{{ facet.facetType.startsWith }}&CloseCurlyDoubleQuote;
            </div>
          </template>
          <template v-else-if="'contains' in facet.facetType">
            <div class="filter-type-glyph">
              contains
            </div>
            <div class="filter-type-content">
              &OpenCurlyDoubleQuote;{{ facet.facetType.contains }}&CloseCurlyDoubleQuote;
            </div>
          </template>
        </div>
      </template>
      <template #menu="{ }">
        <dropdown-menu class="filter-menu-container">
          <div class="filter-menu-dropdown">
            <menu-section>
              <div class="filter-menu-dropdown-title">
                <div class="filter-menu-dropdown-type">
                  {{ name }}
                </div>
                <menu-selection
                  v-if="[PropertySchema.UtcTimestampMillisValue].includes(facetSchema!)"
                  :model-value="searchFacetType(facet)"
                  :label="k => searchFacetTypeName(k)"
                  :options="[
                    'exists' as SearchFacetTypeKey,
                    'afterDate' as SearchFacetTypeKey,
                    'onOrAfterDate' as SearchFacetTypeKey,
                    'beforeDate' as SearchFacetTypeKey,
                    'onOrBeforeDate' as SearchFacetTypeKey,
                  ]"
                  @update:model-value="(key: SearchFacetTypeKey) => updateFacetType(key)"
                />
                <menu-selection
                  v-else-if="[PropertySchema.PositiveIntegerValue, PropertySchema.AnyIntegerValue, PropertySchema.PositiveFloatValue, PropertySchema.NonNegativeIntegerValue, PropertySchema.SizeBytesValue].includes(facetSchema!)"
                  :model-value="searchFacetType(facet)"
                  :label="k => searchFacetTypeName(k)"
                  :options="[
                    'exists' as SearchFacetTypeKey,
                    'greaterThan' as SearchFacetTypeKey,
                    'greaterThanEqual' as SearchFacetTypeKey,
                    'lessThan' as SearchFacetTypeKey,
                    'lessThanEqual' as SearchFacetTypeKey,
                  ]"
                  @update:model-value="(key: SearchFacetTypeKey) => updateFacetType(key)"
                />
                <menu-selection
                  v-else
                  :model-value="searchFacetType(facet)"
                  :label="k => searchFacetTypeName(k)"
                  :options="[
                    'exists' as SearchFacetTypeKey,
                    'equals' as SearchFacetTypeKey,
                    'notEquals' as SearchFacetTypeKey,
                    'contains' as SearchFacetTypeKey,
                    'startsWith' as SearchFacetTypeKey,
                  ]"
                  @update:model-value="(key: SearchFacetTypeKey) => updateFacetType(key)"
                />
              </div>
            </menu-section>
            <menu-separator v-if="!('exists' in facet.facetType)" />
            <div v-if="'exists' in facet.facetType" />
            <dropdown-text-input
              v-if="'equals' in facet.facetType"
              v-model="facet.facetType.equals"
              placeholder="Enter a value..."
            />
            <dropdown-text-input
              v-if="'notEquals' in facet.facetType"
              v-model="facet.facetType.notEquals"
              placeholder="Enter a value..."
            />
            <dropdown-text-input
              v-if="'contains' in facet.facetType"
              v-model="facet.facetType.contains"
              placeholder="Enter a value..."
            />
            <dropdown-text-input
              v-if="'startsWith' in facet.facetType"
              v-model="facet.facetType.startsWith"
              placeholder="Enter a value..."
            />
            <dropdown-number-input
              v-if="'greaterThan' in facet.facetType"
              v-model="facet.facetType.greaterThan"
              placeholder="Enter a value..."
            />
            <dropdown-number-input
              v-if="'greaterThanEqual' in facet.facetType"
              v-model="facet.facetType.greaterThanEqual"
              placeholder="Enter a value..."
            />
            <dropdown-number-input
              v-if="'lessThan' in facet.facetType"
              v-model="facet.facetType.lessThan"
              placeholder="Enter a value..."
            />
            <dropdown-number-input
              v-if="'lessThanEqual' in facet.facetType"
              v-model="facet.facetType.lessThanEqual"
              placeholder="Enter a value..."
            />
            <dropdown-date-time-input
              v-if="'beforeDate' in facet.facetType"
              v-model="facet.facetType.beforeDate"
              placeholder="Enter a value..."
            />
            <dropdown-date-time-input
              v-if="'onOrBeforeDate' in facet.facetType"
              v-model="facet.facetType.onOrBeforeDate"
              placeholder="Enter a value..."
            />
            <dropdown-date-time-input
              v-if="'afterDate' in facet.facetType"
              v-model="facet.facetType.afterDate"
              placeholder="Enter a value..."
            />
            <dropdown-date-time-input
              v-if="'onOrAfterDate' in facet.facetType"
              v-model="facet.facetType.onOrAfterDate"
              placeholder="Enter a value..."
            />
          </div>
        </dropdown-menu>
      </template>
    </dropdown>
    <div class="filter-picker-close" @click="onRemoveFilter()">
      <icon-action type="cross" size="2xs" />
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.filter-picker-container
  @apply flex flex-none items-center gap-[#{$menu-search-gap-item-facet}] relative
  @apply h-[24px]
  @apply rounded-[#{$menu-br-item}] overflow-hidden
  @apply t-menu-sm

.filter-picker-description
  @apply flex flex-none items-center gap-1
  @apply h-full px-2

.filter-picker-close
  @apply flex flex-none items-center
  @apply h-full px-1

.filter-picker-description
  @apply bg-$global-blue-accent
  @apply text-$global-blue-primary

.filter-picker-close
  @apply bg-$global-blue-accent
  @apply text-$global-blue-primary
  @apply cursor-pointer
  &:hover
    @apply bg-$global-blue-secondary

.filter-picker-button
  @apply flex-none flex items-center gap-[#{$menu-search-gap-item-facet-section}]
  @apply cursor-pointer
  @apply h-[24px]
  @apply px-2 bg-$global-blue-accent
  @apply text-$global-blue-primary
  &:hover
    @apply bg-$global-blue-secondary

.filter-picker-view
  @apply w-3 h-3 aspect-square rounded-full

.filter-menu-container
  @apply mt-2

.filter-menu-dropdown
  @apply flex flex-col
  @apply overflow-auto
  max-height: calc(100vh - 60px)

.filter-menu-dropdown-title
  @apply flex gap-2 items-center h-[28px] -my-1.5
.filter-menu-dropdown-type
  @apply text-[#{$menu-t-header}] t-menu-sm
</style>
